/**
 * Signin.js - Login success: store API_SIGN_IN response and navigate to HistopathologyHome.
 * No second API; all storage from sign-in response only.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import eMedHub from "../Assets/Logo/eMedHub.jpeg";
import { Box, Button, Checkbox, FormControlLabel, IconButton, TextField, Typography } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import Logo from "../Assets/Logo/Logo.svg";
import "./Signin.css";
import "./Forgotpassword.css";
import RestAPIService from "../Utility/Services/RestAPIService";
import { setCachevalue, clearAllCachevalue } from "../Utility/Services/CacheProviderService";
import { Serviceurls } from "../Utility/API/Serviceurls";
import Dialog from "../Components/Dialog";
import Loader from "../Components/Loader";
import Failed from "../Assets/Animation/Failed.gif";
import Success from "../Assets/Animation/Success.gif";
import ToastMsg from "../Components/ToastMsg/ToastMsg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { ConfirmPopup } from "../Components/Common Components/CommonComponents";
import { usePermissions } from "../Utility/PermissionContext";
import { getFirstAllowedRoute } from "../Utility/Services/RoleAccessService";

const t = (key) => ({ userName: "User Name", Password: "Password", Message: "Message", Close: "Close" }[key] || key);

export default function Signin() {
  const navigate = useNavigate();
  const { updatePermissions } = usePermissions();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [usernameHelperText, setUsernameHelperText] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordHelperText, setPasswordHelperText] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);
  const [isResetPass, setIsResetPass] = useState(false);
  const [isResetDays, setIsResetDays] = useState("0");
  const [skipforNow, setSkipforNow] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [isSuccessMsg, setIsSuccessMsg] = useState(false);
  const [isSuccessMsgText, setIsSuccessMsgText] = useState("");

  useEffect(() => {
    clearAllCachevalue();
  }, []);

  const validate = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "username") {
      setUsername(value);
      setUsernameError("");
      setUsernameHelperText("");
    }
    if (name === "password") {
      setPassword(value);
      setPasswordError("");
      setPasswordHelperText("");
    }
  }, []);

  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setIsSuccessMsg(false);
    setIsSuccessMsgText("");
  }, []);

  const dialogCloseFunction = useCallback(() => {
    window.location.reload();
    setLoginSuccess(false);
  }, []);

  const failesDialogClose = useCallback(() => {
    setOpenFailed(false);
  }, []);

  const resetPassword = useCallback(() => {
    navigate("/ResetPassword", { state: { userName: username } });
  }, [navigate, username]);

  const submit = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      try {
        if (username && password) {
          clearAllCachevalue();
          setIsLoader(true);
          const data = { user_name: username, password };
          RestAPIService.create(data, Serviceurls.API_SIGN_IN)
            .then((response) => {
              // Support both wrapped ({ status, data }) and flat ({ token, user_id, ... }) response
              const reply = response?.data ?? response;
              const d = reply?.data ?? reply;
              const isSuccess = d && (d.token != null || d.user_id != null);

              if (isSuccess) {
                if (d.is_first_login) {
                  setIsLoader(false);
                  navigate("/ResetPassword", { state: { userName: username } });
                  return;
                }
                if (!d.is_alert || skipforNow) {
                  setCachevalue(d.token, "LoginAuthToken");
                  setCachevalue("true", "isLoggedIn");
                  setCachevalue(d.token, "AccessToken");
                  setCachevalue(d.user_name ?? "", "LogedUserName");
                  setCachevalue(String(d.user_id ?? ""), "loggedUserId");
                  setCachevalue(String(d.branch_id ?? ""), "BranchId");
                  setCachevalue(d.branch_name ?? d.barnch_name ?? "", "BranchName");
                  setCachevalue(
                    JSON.stringify({
                      token: d.token,
                      user_id: d.user_id,
                      user_name: d.user_name,
                      organization_id: d.organization_id,
                      branch_id: d.branch_id,
                      histopathology_id: d.histopathology_id,
                      module_name: "Histopathology",
                    }),
                    "loggedInUserInfo"
                  );
                  setCachevalue(String(d.auto_logout_minutes ?? 15), "auto_log_out_time");
                  setCachevalue(String(d.transaction_number_mandatory === true), "is_transaction_number_mandatory");
                  setCachevalue(String(d.bill_amount_auto_fetched === true), "auto_fetch_bill_amount_to_payment");
                  setCachevalue(String(d.enable_cash_limit_per_patient === true), "enable_cash_limit_per_patient");
                  setCachevalue(JSON.stringify(d.organization_id ?? null), "org_id");
                  const screenAccess = Array.isArray(d.permission_access) ? d.permission_access : [];
                  setCachevalue(JSON.stringify(screenAccess), "Scrren_access");
                  updatePermissions({
                    ...d,
                    package_access: d?.package_access || { modules: [] },
                  });
                  setIsLoader(false);
                  const firstRoute = getFirstAllowedRoute(screenAccess);
                  navigate(firstRoute, { replace: true, state: { NewLogin: true } });
                  return;
                } else {
                  setIsLoader(false);
                  setIsResetPass(!!d?.is_alert);
                  setIsResetDays(String(d?.balance_days ?? "0"));
                }
              } else {
                setIsLoader(false);
                setOpenFailed(true);
              }
            })
            .catch((err) => {
              if (err?.response?.data?.message === "Invalid username") {
                setIsLoader(false);
                setOpenFailed(false);
                setUsernameError("error");
                setUsernameHelperText(err.response.data.message);
              } else if (err?.response?.data?.message === "Invalid password") {
                setIsLoader(false);
                setOpenFailed(false);
                setPasswordError("error");
                setPasswordHelperText(err.response.data.message);
              } else {
                setIsLoader(false);
                setUsernameError("error");
                setUsernameHelperText(err?.response?.data?.message);
                setIsErrorMsg(true);
                setIsErrorMsgText(err?.response?.data?.message || err?.message || "Error");
                setIsAdmin(!!(err?.response?.data?.is_account_locked && err?.response?.data?.is_admin_user));
                setTimeout(() => setIsErrorMsg(false), 2000);
              }
            });
        } else {
          setCachevalue("false", "isLoggedIn");
          if (!username) {
            setUsernameError("error");
            setUsernameHelperText("Enter Valid username");
          } else {
            setUsernameError("");
            setUsernameHelperText("");
          }
          if (!password) {
            setPasswordError("error");
            setPasswordHelperText("Enter valid password");
          } else {
            setPasswordError("");
            setPasswordHelperText("");
          }
        }
      } catch (err) {
        if (err?.response?.data?.message === "Invalid username") {
          setIsLoader(false);
          setOpenFailed(false);
          setUsernameError("error");
          setUsernameHelperText(err.response.data.message);
        } else if (err?.response?.data?.message === "Invalid password") {
          setIsLoader(false);
          setOpenFailed(false);
          setPasswordError("error");
          setPasswordHelperText(err.response.data.message);
        } else {
          setIsErrorMsg(true);
          setIsErrorMsgText(err?.message || "Error");
          setTimeout(() => setIsErrorMsg(false), 2000);
        }
      }
    },
    [username, password, skipforNow, navigate, updatePermissions]
  );

  const popClose = useCallback(() => {
    setIsResetPass(false);
    setSkipforNow(true);
    setTimeout(() => submit(), 0);
  }, [submit]);

  return (
    <div className="total">
      <Loader loaderOpen={isLoader} />
      <div className="left">
        <img src={eMedHub} width="100%" height="100%" alt="login-img" />
      </div>

      <Box className="eMed_Forget_Con">
        <img src={Logo} alt="logo" style={{ width: "6vw", height: "6vw" }} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(e);
          }}
        >
          <Box className="eMed_Forget_Box">
            <Typography
              sx={{
                fontSize: "1.3dvw",
                marginTop: "1vw",
                fontWeight: "bold",
                display: "flex",
                justifyContent: "center",
              }}
            >
              Welcome To eMedHub !
            </Typography>

            <TextField
              focused
              size="small"
              error={!!usernameError}
              helperText={usernameHelperText}
              name="username"
              inputProps={{ emed_tid: "username" }}
              value={username}
              sx={{ margin: "2vw 1vw 1vw 1vw" }}
              onChange={validate}
              label={t("userName")}
            />

            <TextField
              focused
              size="small"
              error={!!passwordError}
              helperText={passwordHelperText}
              value={password}
              sx={{ margin: "1vw 1vw 1vw 1vw" }}
              inputProps={{ emed_tid: "password" }}
              name="password"
              onChange={validate}
              label={t("Password")}
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ margin: "0vw 2vw 1vw 2vw" }}>
              <FormControlLabel
                sx={{ fontSize: "0.9dvw", color: "#616161" }}
                control={<Checkbox />}
                label="Remember Me"
              />
            </Box>

            <Box display="flex" justifyContent="center">
              <Button id="eMed_Forget_Btn" variant="contained" size="small" emed_tid="signin" type="submit">
                Sign in
              </Button>
            </Box>

            {isAdmin && (
              <Box sx={{ display: "flex", justifyContent: "center", marginBottom: "0.625vw" }}>
                <Typography onClick={() => navigate("/UnlockAccount")} id="eMed_Resent_CodeText">
                  Unlock Your Account ?
                </Typography>
              </Box>
            )}
          </Box>
        </form>

        {loginSuccess && (
          <Dialog
            type="loginsucess"
            DialogClose={dialogCloseFunction}
            header="Message"
            content="Login Successfully !"
            icon={<img className="eMed_dilboxAnimations" alt="Success" src={Success} />}
            secondary="Close"
          />
        )}

        {openFailed && (
          <Dialog
            type="Notification"
            DialogClose={failesDialogClose}
            header="Message"
            content={usernameHelperText}
            icon={<img className="eMed_dilboxAnimations" alt="Failed" src={Failed} />}
            secondary="Close"
            closeButton={dialogCloseFunction}
          />
        )}
      </Box>

      {isErrorMsg && (
        <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />
      )}
      {isSuccessMsg && (
        <ToastMsg severity="success" msg={isSuccessMsgText} msgPop={msgClose} />
      )}

      {isResetPass && (
        <ConfirmPopup
          cnfTitle="Reset Password"
          cnfContent={
            isResetDays === "0" || isResetDays === 0
              ? "Your password will expire, so please reset your password."
              : `Your password will expire in ${isResetDays} days, so please reset your password.`
          }
          contentBold="bold"
          cnfAlertPopupClose={popClose}
          btnText1="Skip for Now"
          btnText2="Reset Password"
          btnType="error"
          btn1Hide={isResetDays === "0" || isResetDays === 0}
          cnfData={resetPassword}
        />
      )}
    </div>
  );
}
